import { useState } from "react";
import { useTranslation } from "react-i18next";
import ReportSheet from "../common/ReportSheet";
import { openExternal } from "../../utils/openExternal";
import type { User, Subscription, SectionKey, ContinueLesson, Badges } from "../../types";
import { Icon, BeeMark } from "../common/ui";
import { useAppConfig } from "../../config/AppConfigContext";

const SOCIALS: { key: string; icon: string; label: string; handle: string; url: string }[] = [
  { key: "site", icon: "globe", label: "Website", handle: "beecompany.by", url: "https://beecompany.by/" },
  { key: "instagram", icon: "instagram", label: "Instagram", handle: "@beecompany.by", url: "https://www.instagram.com/beecompany.by/" },
  { key: "tiktok", icon: "music", label: "TikTok", handle: "@beecompany_by", url: "https://www.tiktok.com/@beecompany_by?_t=8lScxAgWTh7&_r=1" },
  { key: "youtube", icon: "youtube", label: "YouTube", handle: "@beecompanyby", url: "https://www.youtube.com/@beecompanyby" },
  { key: "threads", icon: "atSign", label: "Threads", handle: "@beecompany.by", url: "https://www.threads.com/@beecompany.by?xmt=AQG0mPWlSRZkjfMpSTiWwEGwrZLdL83_uY8h2nIrMmHW654" },
  { key: "vk", icon: "link", label: "VK", handle: "marmalade_anka", url: "https://vk.com/marmalade_anka" },
];

interface Props {
  /** For «Сообщить о проблеме». */
  token: string;
  me: User | null;
  subscription: Subscription | null;
  onStartSelection: () => void;
  onStudy: () => void;
  continueLesson?: ContinueLesson | null;
  onContinue?: (lessonId: string) => void;
  onHistory: () => void;
  onGames: () => void;
  onAdminSettings: () => void;
  onAdminInvites?: () => void;
  onAdminUsers?: () => void;
  badges?: Badges | null;
  isAdmin: boolean;
  onAccount: () => void;
  /** ⚡ header button — subscription only, not the whole profile. */
  onSubscription?: () => void;
  onLogout: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}

export default function MainMenu({
  token, me, subscription, onStartSelection, onStudy, continueLesson, onContinue, onHistory, onGames, onAdminSettings, onAdminInvites, onAdminUsers, badges, isAdmin, onAccount, onSubscription, theme, onThemeChange,
}: Props) {
  const { t, i18n } = useTranslation();
  const name = me?.first_name || me?.username || "";
  const [showFind, setShowFind] = useState(false);
  const [reporting, setReporting] = useState(false);
  const { config } = useAppConfig();

  /** A section the admin switched off stays visible but inert, marked «Скоро».
   *  Admins keep every section live so they can check it before launch. */
  const isSoon = (key: string) =>
    !isAdmin && config.sections[key as SectionKey] === false;

  const announcement = config.announcement;
  const announcementText = i18n.language?.startsWith("en")
    ? (announcement.text_en || announcement.text_ru)
    : (announcement.text_ru || announcement.text_en);

  const cards = [
    {
      key: "selection", accent: "amber", icon: "palette",
      title: t("menu.startSelection"), desc: t("menu.startSelectionDesc"),
      chips: [
        { i: "droplet", l: "Pigment" },
        { i: "flask", l: "Mix" },
        { i: "layers", l: "Layup" },
      ],
      onClick: onStartSelection,
    },
    {
      key: "study", accent: "violet", icon: "graduation",
      title: t("menu.study"), desc: t("menu.studyDesc"),
      chips: [
        { i: "play", l: t("menu.lessonsChip") },
        { i: "heart", l: "Anya" },
      ],
      onClick: onStudy,
    },
    {
      key: "history", accent: "amber", icon: "history",
      title: t("menu.history"), desc: t("menu.historyDesc"),
      chips: [
        { i: "search", l: t("history.searchChip") },
        { i: "calendar", l: t("history.dateChip") },
      ],
      onClick: onHistory,
    },
    {
      key: "games", accent: "violet", icon: "gamepad",
      title: t("menu.games"), desc: t("menu.gamesDesc"),
      chips: [
        { i: "trophy", l: t("games.quiz.chip") },
        { i: "brush", l: t("games.stroke.chip") },
      ],
      onClick: onGames,
    },
    // Chat with Anya, the task-review queue and the participant chats all moved
    // behind the profile button — they are inbox-shaped, not destinations.
    ...(isAdmin ? [{
      key: "adminsettings", accent: "amber", icon: "sliders",
      title: t("adminSettings.title"), desc: t("adminSettings.menuDesc"),
      chips: [
        { i: "toggle", l: t("adminSettings.sectionsChip") },
        { i: "wallet", l: t("adminSettings.pricesChip") },
      ],
      onClick: onAdminSettings,
    }, {
      key: "admininvites", accent: "violet", icon: "ticket",
      title: t("invite.adminTitle"), desc: t("invite.menuDesc"),
      chips: [
        { i: "link", l: t("invite.chipLink") },
        { i: "send", l: "Telegram" },
      ],
      onClick: onAdminInvites ?? (() => {}),
    }, {
      key: "adminusers", accent: "amber", icon: "users",
      title: t("adminUsers.title"), desc: t("adminUsers.menuDesc"),
      chips: [
        { i: "search", l: t("adminUsers.chipSearch") },
        { i: "graduation", l: t("adminUsers.chipProgress") },
      ],
      onClick: onAdminUsers ?? (() => {}),
    }] : []),
  ];

  // Subscription status chip for the header
  const subBadge = subscription?.is_active
    ? `${subscription.days_left} ${t("account.daysAbbr")}`
    : null;

  // Chat and the admin queues now live behind the profile button, so its badge
  // is the sum of everything waiting there.
  const badgeTotal = badges?.total ?? 0;

  return (
    <div className="beeapp__scroll">
      {/* Custom header with account button */}
      <header className={"between menu-header" + (subBadge ? " has-days" : "")} style={{
        position: "sticky", top: 0, zIndex: 10,
        backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        background: "linear-gradient(to bottom, var(--ambient), transparent)",
      }}>
        <div className="row menu-header__brand" style={{ gap: 10 }}>
          <BeeMark size={38} />
          <div className="t-h menu-header__title">{t("app.title")}</div>
        </div>
        <div className="row menu-header__actions">
          {/* Subscription — golden lightning, with days left when active */}
          <button
            onClick={onSubscription ?? onAccount}
            aria-label={t("account.subscription")}
            className={"icon-btn sub-btn" + (subBadge ? " sub-btn--days" : "")}
            style={{
              position: "relative",
              background: "linear-gradient(165deg, var(--accent-fill), var(--accent-fill-2))",
              color: "var(--accent-ink)", border: "none",
              boxShadow: "0 2px 10px var(--accent-glow), inset 0 1px 0 rgba(255,255,255,0.45)",
            }}
          >
            <Icon name="zap" size={18} stroke={2.2} />
            {subBadge && (
              <span style={{ fontSize: "0.76rem", fontWeight: 800, whiteSpace: "nowrap" }}>
                {subBadge}
              </span>
            )}
          </button>

          {/* Account / profile — also the way into the chat and the admin queues,
              so its badge counts everything waiting behind it. */}
          <button
            className="icon-btn"
            onClick={onAccount}
            aria-label={t("account.title")}
            style={{ position: "relative" }}
          >
            <Icon name="user" size={19} />
            {badgeTotal > 0 && (
              <span
                aria-label={t("account.pendingCount", { count: badgeTotal })}
                style={{
                  position: "absolute", top: -4, right: -4, minWidth: 17, height: 17,
                  display: "grid", placeItems: "center", padding: "0 4px",
                  background: "var(--violet-fill)", color: "var(--violet-ink)",
                  borderRadius: 99, fontSize: "0.62rem", fontWeight: 800, lineHeight: 1,
                  boxShadow: "0 2px 6px var(--violet-glow)",
                }}
              >
                {badgeTotal > 9 ? "9+" : badgeTotal}
              </span>
            )}
          </button>
          {/* Theme + lang from AppHeader shared components */}
          <div className="seg menu-header__lang" role="group" aria-label="Language">
            {(["en", "ru"] as const).map((l) => (
              <button
                key={l}
                aria-pressed={i18n.language?.startsWith(l)}
                onClick={() => i18n.changeLanguage(l)}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            className="icon-btn"
            onClick={() => onThemeChange(theme === "dark" ? "light" : "dark")}
            aria-label={t("common.theme")}
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} size={20} />
          </button>
        </div>
      </header>

      <div className="wrap content-cap" style={{ paddingTop: 8 }}>
        <div className="anim-rise" style={{ marginBottom: 22 }}>
          <p className="eyebrow" style={{ marginBottom: 6 }}>{t("menu.greeting")}</p>
          {name && <h1 className="t-display">{name}</h1>}
        </div>

        {/* Admin-authored announcement (e.g. a promo or a heads-up) */}
        {announcement.enabled && announcementText && (
          <div
            className="glass anim-rise"
            style={{
              padding: "12px 16px", marginBottom: 16, borderRadius: "var(--r-md)",
              display: "flex", alignItems: "flex-start", gap: 11,
              background: "var(--accent-tint)",
            }}
          >
            <span style={{ color: "var(--accent-text)", flex: "none", marginTop: 1, display: "inline-flex" }}>
              <Icon name="sparkles" size={18} />
            </span>
            <p className="t-sm" style={{ margin: 0, fontWeight: 700, lineHeight: 1.45 }}>
              {announcementText}
            </p>
          </div>
        )}

        {/* Subscription notice for users without subscription */}
        {subscription === null && (
          <div
            className="glass anim-rise"
            style={{
              padding: "12px 16px", marginBottom: 16, borderRadius: "var(--r-md)",
              display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
            }}
            onClick={onSubscription ?? onAccount}
            role="button"
          >
            <Icon name="sparkles" size={20} />
            <div style={{ flex: 1 }}>
              <p className="t-sm" style={{ margin: 0, fontWeight: 700 }}>
                {t("menu.lockedLessons")}
              </p>
              <p className="t-xs muted-2" style={{ margin: 0 }}>
                {t("menu.learnSubscription")}
              </p>
            </div>
            <Icon name="chevronRight" size={16} />
          </div>
        )}

        {/* Continue watching — resume the last lesson video where you stopped */}
        {continueLesson && onContinue && !isSoon("study") && (() => {
          const ctitle = i18n.language?.startsWith("en")
            ? (continueLesson.title_en || continueLesson.title_ru)
            : (continueLesson.title_ru || continueLesson.title_en);
          const pct = continueLesson.duration && continueLesson.duration > 0
            ? Math.min(100, Math.round((continueLesson.position / continueLesson.duration) * 100))
            : null;
          return (
            <button
              onClick={() => onContinue(continueLesson.lesson_id)}
              className="glass glass-lg glass-sheen anim-rise"
              style={{
                width: "100%", border: "none", color: "inherit", cursor: "pointer",
                padding: 16, marginBottom: 16, display: "flex", alignItems: "center", gap: 13,
                textAlign: "left",
              }}
            >
              <span style={{
                display: "grid", placeItems: "center", width: 46, height: 46, borderRadius: 13, flex: "none",
                background: "linear-gradient(165deg, var(--violet-fill), var(--violet-fill-2))",
                color: "var(--violet-ink)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
              }}>
                <Icon name="play" size={22} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="eyebrow" style={{ marginBottom: 3 }}>{t("menu.continueTitle")}</p>
                <div className="t-h" style={{ lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t("menu.lessonN", { n: continueLesson.order })} · {ctitle}
                </div>
                {pct !== null && (
                  <div className="progress" style={{ marginTop: 8 }}>
                    <div className="progress__fill" style={{
                      width: `${pct}%`,
                      background: "linear-gradient(90deg, var(--violet-fill-2), var(--violet-fill))",
                    }} />
                  </div>
                )}
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
          );
        })()}

        <div className="grid-menu stagger">
          {cards.map((c) => {
            const soon = isSoon(c.key);
            return (
            <button
              key={c.key}
              onClick={soon ? undefined : c.onClick}
              disabled={soon}
              aria-disabled={soon}
              className="glass glass-lg glass-sheen"
              style={{
                textAlign: "left", cursor: soon ? "default" : "pointer", padding: 22, color: "inherit",
                display: "flex", flexDirection: "column", gap: 16, minHeight: 200,
                position: "relative", overflow: "hidden", border: "none",
                opacity: soon ? 0.5 : 1,
                filter: soon ? "saturate(0.35)" : undefined,
              }}
            >
              {soon && (
                <span className="badge badge-amber" style={{ position: "absolute", top: 14, right: 14, zIndex: 2 }}>
                  {t("menu.sectionSoon")}
                </span>
              )}
              {/* glow orb */}
              <div style={{
                position: "absolute", top: -40, right: -40, width: 170, height: 170, borderRadius: "50%",
                background: c.accent === "violet"
                  ? "radial-gradient(circle, var(--violet-glow), transparent 70%)"
                  : "radial-gradient(circle, var(--accent-glow), transparent 70%)",
                opacity: 0.9, pointerEvents: "none",
              }} />

              <div className="between" style={{ position: "relative" }}>
                <span style={{
                  display: "grid", placeItems: "center", width: 54, height: 54, borderRadius: 16,
                  color: c.accent === "violet" ? "var(--violet-ink)" : "var(--accent-ink)",
                  background: c.accent === "violet"
                    ? "linear-gradient(165deg, var(--violet-fill), var(--violet-fill-2))"
                    : "linear-gradient(165deg, var(--accent-fill), var(--accent-fill-2))",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
                }}>
                  <Icon name={c.icon} size={28} stroke={1.9} />
                </span>
                <span className="icon-btn" aria-hidden style={{ pointerEvents: "none", visibility: soon ? "hidden" : undefined }}>
                  <Icon name="arrowRight" size={20} />
                </span>
              </div>

              <div style={{ position: "relative", flex: 1 }}>
                <div className="t-title" style={{ marginBottom: 6 }}>{c.title}</div>
                <div className="t-sm muted" style={{ fontWeight: 600, lineHeight: 1.45 }}>{c.desc}</div>
              </div>

              <div className="wraprow" style={{ position: "relative" }}>
                {c.chips.map((ch, i) => (
                  <span key={i} className={`badge ${c.accent === "violet" ? "badge-violet" : "badge-amber"}`}>
                    <Icon name={ch.i} size={12} /> {ch.l}
                  </span>
                ))}
              </div>
            </button>
            );
          })}
        </div>

        {/* Find us — social networks */}
        <div style={{ marginTop: 18, marginBottom: 8 }}>
          <button
            onClick={() => setShowFind((v) => !v)}
            aria-expanded={showFind}
            className="glass glass-lg"
            style={{
              width: "100%", border: "none", color: "inherit", cursor: "pointer",
              padding: "14px 16px", display: "flex", alignItems: "center", gap: 12,
            }}
          >
            <span style={{
              display: "grid", placeItems: "center", width: 38, height: 38, borderRadius: 11, flex: "none",
              background: "var(--accent-tint)", color: "var(--accent-text)",
            }}>
              <Icon name="mapPin" size={19} />
            </span>
            <div style={{ flex: 1, textAlign: "left" }}>
              <div className="t-h">{t("menu.findUs")}</div>
              <div className="t-xs muted-2">{t("menu.findUsDesc")}</div>
            </div>
            <span style={{
              flex: "none", color: "var(--text-3)",
              transition: "transform 0.3s var(--ease-soft)",
              transform: showFind ? "rotate(180deg)" : "none",
            }}>
              <Icon name="chevronDown" size={20} />
            </span>
          </button>

          {showFind && (
            <div className="anim-fade" style={{ marginTop: 10, display: "grid", gap: 8 }}>
              {SOCIALS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => openExternal(s.url)}
                  className="glass"
                  style={{
                    width: "100%", border: "none", color: "inherit", cursor: "pointer",
                    padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, borderRadius: "var(--r-md)",
                  }}
                >
                  <span style={{
                    display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: 10, flex: "none",
                    background: "var(--glass-1)", color: "var(--accent-text)",
                  }}>
                    <Icon name={s.icon} size={18} />
                  </span>
                  <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                    <div className="t-sm" style={{ fontWeight: 700 }}>{s.label}</div>
                    <div className="t-xs muted-2" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.handle}</div>
                  </div>
                  <Icon name="arrowRight" size={16} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Something wrong or unclear — straight to the team's review chat. */}
        <button className="report-entry" onClick={() => setReporting(true)}>
          <span className="report-entry__icon"><Icon name="flag" size={18} /></span>
          <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
            <span className="t-sm" style={{ fontWeight: 800, display: "block" }}>{t("report.menuTitle")}</span>
            <span className="t-xs muted-2">{t("report.menuDesc")}</span>
          </span>
          <Icon name="chevronRight" size={18} />
        </button>
      </div>
      {reporting && <ReportSheet token={token} screen={t("report.screenMenu")} onClose={() => setReporting(false)} />}
    </div>
  );
}
